import {
    DataRequestRoutesComponent,
    DataRequestTabsComponent,
    RequestHeaderContainerComponent
} from '@vivli/features/data-requests/components';
import { DataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestContextWrapper, StatusUpdateContextWrapper } from '@vivli/features/data-requests/infrastructure/wrappers';
import { NotificationContextWrapper } from '@vivli/shared/features/notification/infrastructure/wrappers';
import { LoadIndicatorCenteredView } from '@vivli/shared/views';
import { dataRequestShellBodyContainerStyle, dataRequestShellContainerStyle } from './data-request-shell.styles';
import { useActiveUserContext } from '@vivli/core/infrastructure/context';
import {
    useDataRequestAdminPath,
    useDataRequestDefaultTabHook,
    useDataRequestForm
} from '@vivli/features/data-requests/infrastructure/hook';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { FormProvider } from 'react-hook-form';
import { Navigate, useParams } from 'react-router';
import {useEffect} from "react";

interface DataRequestShellComponentProps {
    dataRequest: IDataRequest;
    isInPreviewMode?: boolean;
}

//this is a common feature used by the 2 data request features,
//standard feature that uses the GUID to fill the data request
//and vivli ID feature (for use with url access) that uses
//vivli ID to fill the data request.
export const DataRequestShellFeature = ({ dataRequest, isInPreviewMode = false }: DataRequestShellComponentProps) => {
    const { hasPermission } = useActiveUserContext();
    const formApi = useDataRequestForm(dataRequest, hasPermission(PermissionsEnum.DrActAdv));
    const { status } = useParams();
    const { getDefaultTabRoute, getCanonicalRouteBase } = useDataRequestDefaultTabHook();
    const isAdminRoute = useDataRequestAdminPath();

    useEffect(() => {
        if (!dataRequest) return;

        formApi.reset(dataRequest);
    }, [dataRequest]);

    return (
        <div style={dataRequestShellContainerStyle}>
            <FormProvider {...formApi}>
                <DataRequestContextWrapper dataRequest={dataRequest}>
                    <DataRequestContext.Consumer>
                        {({ organizations }) => {
                            if (!organizations) {
                                return <LoadIndicatorCenteredView />;
                            }

                            //DEV-988: this shell renders the read-only tabs nav, and the draft form nav
                            //lives behind a different url segment. Nothing recomputes that segment on a
                            //page load, so a draft reached through an admin url (email link, dashboard
                            //card, or a refresh of one) showed the read-only tab list with the form tabs
                            //missing. Send it to the view the request actually belongs in.
                            //Two exemptions: preview mode is a print window opened at a fixed url, and
                            //'myDataRequestDetailsRO' asks for the read-only view by name, so honour it
                            //rather than bouncing its (external) callers into the editable form.
                            const canonicalRouteBase = getCanonicalRouteBase(dataRequest, organizations);
                            if (!isInPreviewMode && isAdminRoute && canonicalRouteBase === 'myDataRequestDetails') {
                                return <Navigate to={getDefaultTabRoute(dataRequest, organizations, status)} replace />;
                            }

                            return (
                                <StatusUpdateContextWrapper>
                                    <NotificationContextWrapper>
                                        <>
                                            {!isInPreviewMode && <RequestHeaderContainerComponent />}

                                            <div style={dataRequestShellBodyContainerStyle} className="do-not-print">
                                                {!isInPreviewMode && <DataRequestTabsComponent />}
                                                <DataRequestRoutesComponent />
                                            </div>
                                        </>
                                    </NotificationContextWrapper>
                                </StatusUpdateContextWrapper>
                            );
                        }}
                    </DataRequestContext.Consumer>
                </DataRequestContextWrapper>
            </FormProvider>
        </div>
    );
};
